// src/pages/Expenses.jsx
import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import '../styles/Expenses.css';
import { SERVER_URL } from './../config';
import { AuthContext } from './../AuthContext';
import Navbar from './Navbar';

const sanitizeSourceValue = (value) => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
};

const Expenses = () => {
  const { token, isAuthenticated } = useContext(AuthContext);

  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editableExpense, setEditableExpense] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Receipt attachments
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptUploadMsg, setReceiptUploadMsg] = useState('');

  // Create flow receipt
  const [newReceiptFile, setNewReceiptFile] = useState(null);
  const [newReceiptMsg, setNewReceiptMsg] = useState('');

  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    date: '',
    type: 'expected',
    active: true,
    category_id: '',
    from_source: '',
  });

  const [categories, setCategories] = useState([]);
  const [accountsOptions, setAccountsOptions] = useState([]);

  // Filtering states
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchExpenses = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}/expenses/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(response.data)) {
        const normalized = response.data.map((expense) => ({
          ...expense,
          from_source: sanitizeSourceValue(expense.from_source),
        }));
        setExpenses(normalized);
      }
      else console.error('Unexpected response format:', response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [token, isAuthenticated]);

  const fetchCategories = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}/categories/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(response.data)) setCategories(response.data);
      else console.error('Unexpected categories response format:', response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [token, isAuthenticated]);

  const fetchAccountOptions = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const resp = await axios.get(`${SERVER_URL}/accounts/options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(resp.data)) setAccountsOptions(resp.data);
      else console.error('Unexpected accounts/options format:', resp.data);
    } catch (e) {
      console.error('Failed to load account options', e);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchExpenses();
      fetchCategories();
      fetchAccountOptions();
    }
  }, [isAuthenticated, fetchExpenses, fetchCategories, fetchAccountOptions]);

  const applyFilters = useCallback(() => {
    const filtered = expenses.filter((expense) => {
      const activeMatch = (showActive && expense.active) || (showInactive && !expense.active);
      return activeMatch;
    });
    setFilteredExpenses(filtered);
  }, [expenses, showActive, showInactive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'showActive') setShowActive(checked);
    if (name === 'showInactive') setShowInactive(checked);
  };

  const openEditModal = (expense) => {
    const sanitizedExpense = {
      ...expense,
      from_source: sanitizeSourceValue(expense.from_source),
    };
    setSelectedExpense(sanitizedExpense);
    setEditableExpense({ ...sanitizedExpense });
    setUpdateMessage('');
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedExpense(null);
    setEditableExpense(null);
    setUpdateMessage('');
    setReceiptFile(null);
    setReceiptUploadMsg('');
    setReceiptUploading(false);
    setIsEditModalOpen(false);
  };

  const uploadReceiptForExpense = async (expenseId, fileObj, { onDone } = {}) => {
    if (!expenseId || !fileObj) return;
    if (!token || !isAuthenticated) return;

    setReceiptUploading(true);

    try {
      const form = new FormData();
      form.append('file', fileObj);

      await axios.post(`${SERVER_URL}/expenses/${expenseId}/receipts`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      await fetchExpenses();

      if (typeof onDone === 'function') {
        await onDone();
      }

      return true;
    } catch (e) {
      console.error('Receipt upload failed', e);
      return false;
    } finally {
      setReceiptUploading(false);
    }
  };

  const uploadReceipt = async () => {
    if (!selectedExpense || !receiptFile) return;

    setReceiptUploadMsg('');

    const ok = await uploadReceiptForExpense(selectedExpense.id, receiptFile, {
      onDone: async () => {
        const refreshed = await axios.get(`${SERVER_URL}/expenses/${selectedExpense.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const updatedExpense = {
          ...refreshed.data,
          from_source: sanitizeSourceValue(refreshed.data?.from_source),
        };

        setSelectedExpense(updatedExpense);
        setEditableExpense(updatedExpense);
      },
    });

    if (ok) {
      setReceiptFile(null);
      setReceiptUploadMsg('Receipt uploaded.');
    } else {
      setReceiptUploadMsg('Failed to upload receipt.');
    }
  };

  const openReceipt = async (receipt) => {
    if (!receipt?.url) return;
    if (!token || !isAuthenticated) return;

    try {
      // Direct <a href> won't include Authorization header, so we fetch as blob with JWT.
      const resp = await axios.get(`${SERVER_URL}${receipt.url}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(resp.data);
      // Open in a new tab (same behavior as normal links)
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Failed to open receipt', e);
      setReceiptUploadMsg('Failed to open receipt (auth).');
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setEditableExpense((prev) => ({
      ...prev,
      [name]: name === 'from_source' ? sanitizeSourceValue(newValue) : newValue,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedExpense) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/expenses/${selectedExpense.id}/`,
        editableExpense,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const updatedExpense = {
        ...response.data,
        from_source: sanitizeSourceValue(response.data?.from_source),
      };
  setExpenses((prev) => prev.map((x) => (x.id === selectedExpense.id ? updatedExpense : x)));
  setSelectedExpense(updatedExpense);
  setEditableExpense(updatedExpense);
      setUpdateMessage('Expense updated successfully.');
    } catch (error) {
      console.error('Error updating expense:', error);
      setUpdateMessage('Failed to update expense. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${SERVER_URL}/expenses/${selectedExpense.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExpenses((prev) => prev.filter((x) => x.id !== selectedExpense.id));
      closeEditModal();
    } catch (error) {
      console.error('Error deleting expense:', error);
      setUpdateMessage('Failed to delete expense. Please try again.');
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setNewExpense((prev) => ({
      ...prev,
      [name]: name === 'from_source' ? sanitizeSourceValue(newValue) : newValue,
    }));
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.post(`${SERVER_URL}/expenses/`, newExpense, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const createdExpense = {
        ...response.data,
        from_source: sanitizeSourceValue(response.data?.from_source),
      };
      setExpenses((prev) => [...prev, createdExpense]);
      const createdId = response.data?.id;

      // If user picked a receipt file in the create modal, upload it right away
      if (createdId && newReceiptFile) {
        setNewReceiptMsg('Uploading receipt…');
        const ok = await uploadReceiptForExpense(createdId, newReceiptFile);
        setNewReceiptMsg(ok ? 'Receipt uploaded.' : 'Failed to upload receipt.');
      }

      setIsCreateModalOpen(false);
      setNewExpense({
        description: '',
        amount: '',
        date: '',
        type: 'expected',
        active: true,
        category_id: '',
        from_source: '',
      });
      setNewReceiptFile(null);
      setNewReceiptMsg('');
    } catch (error) {
      console.error('Error creating expense:', error);
      setNewReceiptMsg('Failed to create expense.');
    }
  };

  const formatDateLocal = (dateString) => {
    if (!dateString) return '';
    const [y, m, d] = dateString.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const totalAmount = useMemo(
    () => filteredExpenses.reduce((sum, x) => sum + parseFloat(x.amount), 0),
    [filteredExpenses]
  );

  const handleActiveChange = async (expenseId, currentStatus) => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/expenses/${expenseId}/`,
        { active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.status === 200) {
        const updatedExpense = {
          ...response.data,
          from_source: sanitizeSourceValue(response.data?.from_source),
        };
        setExpenses((prev) => prev.map((x) => (x.id === expenseId ? updatedExpense : x)));
        setFilteredExpenses((prev) => prev.map((x) => (x.id === expenseId ? updatedExpense : x)));
      }
    } catch (error) {
      console.error('Error updating active status:', error);
      alert('Failed to update active status. Please try again.');
    }
  };

  // Selection logic
  const handleExpenseSelection = (expenseId, checked) => {
    setSelectedExpenses((prev) => (checked ? [...prev, expenseId] : prev.filter((id) => id !== expenseId)));
  };

  const confirmCopySelectedToPayments = () => setShowConfirmModal(true);
  const cancelCopyToPayments = () => setShowConfirmModal(false);

  const copySelectedToPayments = async () => {
    if (selectedExpenses.length === 0) {
      alert('Please select at least one expense to copy.');
      return;
    }
    try {
      const response = await axios.post(
        `${SERVER_URL}/expenses/copy_array_to_payments/`,
        { expense_ids: selectedExpenses },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      alert(response.data.message || 'Copied successfully.');
      setSelectedExpenses([]);
    } catch (error) {
      console.error('Error copying expenses to payments:', error);
      alert('Failed to copy expenses. Please try again.');
    } finally {
      setShowConfirmModal(false);
    }
  };

  return (
    <div className="expenses">
      <Navbar />
      <div className="content">
          <h1>Expenses</h1>

          <div className="actions-bar">
            <button className="create-expense-button" onClick={() => setIsCreateModalOpen(true)}>
              Add Expense
            </button>
            <button
              className="copy-to-payments-button"
              onClick={confirmCopySelectedToPayments}
              disabled={selectedExpenses.length === 0}
            >
              Copy to Payments
            </button>
          </div>

          <div className="filter-options">
            <label>
              <input type="checkbox" name="showActive" checked={showActive} onChange={handleFilterChange} />
              Show Active
            </label>
            <label>
              <input type="checkbox" name="showInactive" checked={showInactive} onChange={handleFilterChange} />
              Show Inactive
            </label>
          </div>

          <div className="expenses-container">
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Date of Expense</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Category</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense, index) => (
                  <tr
                    key={expense.id}
                    className={index % 2 === 0 ? 'even' : 'odd'}
                    onClick={() => openEditModal(expense)}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedExpenses.includes(expense.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleExpenseSelection(expense.id, e.target.checked)}
                      />
                    </td>
                    <td>{expense.description || 'N/A'}</td>
                    <td>${parseFloat(expense.amount).toFixed(2)}</td>
                    <td>{expense.date ? formatDateLocal(expense.date) : 'N/A'}</td>
                    <td>{expense.type}</td>
                    <td>{sanitizeSourceValue(expense.from_source) || 'N/A'}</td>
                    <td>{expense.category ? expense.category.name : 'No Category'}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={expense.active}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleActiveChange(expense.id, expense.active)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="expenses-tiles">
              {filteredExpenses.map((expense) => (
                <div key={expense.id} className="expense-tile" onClick={() => openEditModal(expense)}>
                  <div className="expense-description">{expense.description || 'N/A'}</div>
                  <div className="expense-amount">
                    <label>Amount: </label>${parseFloat(expense.amount).toFixed(2)}
                  </div>
                  <div className="expense-date">
                    <label>Date: </label>
                    {expense.date ? formatDateLocal(expense.date) : 'N/A'}
                  </div>
                  <div className="expense-type">
                    <label>Type: </label>
                    {expense.type}
                  </div>
                  <div className="expense-source">
                    <label>Source: </label>
                    {sanitizeSourceValue(expense.from_source) || 'N/A'}
                  </div>
                  <div className="expense-category">
                    <label>Category: </label>
                    {expense.category ? expense.category.name : 'No Category'}
                  </div>
                  <div className="expense-active">
                    <label>Active: </label>
                    <input
                      type="checkbox"
                      checked={expense.active}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleActiveChange(expense.id, expense.active)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="total-amount">
            <h2>Total: ${totalAmount.toFixed(2)}</h2>
          </div>
      </div>

      {isEditModalOpen && editableExpense && (
        <div className="modal" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-x"
              onClick={closeEditModal}
              aria-label="Close"
              title="Close"
            >
            </button>
            <h2>Edit Expense</h2>
            <form>
              <label>
                Description:
                <input type="text" name="description" value={editableExpense.description} onChange={handleEditInputChange} />
              </label>
              <label>
                Amount:
                <input type="number" name="amount" value={editableExpense.amount} onChange={handleEditInputChange} step="0.01" />
              </label>
              <label>
                Date:
                <input type="date" name="date" value={editableExpense.date || ''} onChange={handleEditInputChange} />
              </label>
              <label>
                Type:
                <select name="type" value={editableExpense.type} onChange={handleEditInputChange}>
                  <option value="expected">Expected</option>
                  <option value="forced">Forced</option>
                </select>
              </label>
              <label>
                Source:
                <select name="from_source" value={editableExpense.from_source || ''} onChange={handleEditInputChange}>
                  <option value="">Select Source</option>
                  {accountsOptions.map((opt) => {
                    const optionValue = sanitizeSourceValue(opt.value ?? opt.label ?? '');
                    const optionLabel = sanitizeSourceValue(opt.label ?? opt.value ?? '');
                    return (
                      <option key={opt.id} value={optionValue}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label>
                Category:
                <select name="category_id" value={editableExpense.category_id || ''} onChange={handleEditInputChange}>
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inline-checkbox">
                Active:
                <input type="checkbox" name="active" checked={editableExpense.active} onChange={handleEditInputChange} />
              </label>

              <div className="receipts-section">
                <div className="receipts-title">Receipts</div>

                <div className="receipts-list">
                  {editableExpense.receipts && editableExpense.receipts.length > 0 ? (
                    editableExpense.receipts.map((r) => {
                      return (
                        <div key={r.id} className="receipt-item">
                          <button
                            type="button"
                            className="receipt-link"
                            onClick={() => openReceipt(r)}
                            title="Open receipt"
                          >
                            {r.original_filename || `Receipt #${r.id}`}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="receipt-empty">No receipts attached yet.</div>
                  )}
                </div>

                <div className="receipt-upload-row">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  />
                  <button
                    type="button"
                    className="modal-button"
                    onClick={uploadReceipt}
                    disabled={!receiptFile || receiptUploading}
                    title={!receiptFile ? 'Choose a file first' : 'Upload receipt'}
                  >
                    {receiptUploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>

                {receiptUploadMsg && (
                  <div className={`modal-message ${receiptUploadMsg.includes('Failed') ? 'error' : 'success'}`}>
                    {receiptUploadMsg}
                  </div>
                )}
              </div>

              {updateMessage && (
                <div className={`modal-message ${updateMessage.includes('success') ? 'success' : 'error'}`}>
                  {updateMessage}
                </div>
              )}

              <div className="modal-buttons">
                <button type="button" className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button type="button" className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button type="button" className="modal-button close-button" onClick={closeEditModal}>
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <div className="modal" onClick={() => setIsCreateModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-x"
              onClick={() => setIsCreateModalOpen(false)}
              aria-label="Close"
              title="Close"
            >
            </button>
            <h2>Create Expense</h2>
            <form onSubmit={handleCreateExpense}>
              <label>
                Description:
                <input type="text" name="description" value={newExpense.description} onChange={handleNewInputChange} required />
              </label>
              <label>
                Amount:
                <input type="number" name="amount" value={newExpense.amount} onChange={handleNewInputChange} step="0.01" required />
              </label>
              <label>
                Date:
                <input type="date" name="date" value={newExpense.date} onChange={handleNewInputChange} required />
              </label>
              <label>
                Type:
                <select name="type" value={newExpense.type} onChange={handleNewInputChange}>
                  <option value="expected">Expected</option>
                  <option value="forced">Forced</option>
                </select>
              </label>
              <label>
                Source:
                <select name="from_source" value={newExpense.from_source} onChange={handleNewInputChange}>
                  <option value="">Select Source</option>
                  {accountsOptions.map((opt) => {
                    const optionValue = sanitizeSourceValue(opt.value ?? opt.label ?? '');
                    const optionLabel = sanitizeSourceValue(opt.label ?? opt.value ?? '');
                    return (
                      <option key={opt.id} value={optionValue}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label>
                Category:
                <select name="category_id" value={newExpense.category_id} onChange={handleNewInputChange} required>
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inline-checkbox">
                Active:
                <input type="checkbox" name="active" checked={newExpense.active} onChange={handleNewInputChange} />
              </label>

              <div className="receipts-section">
                <div className="receipts-title">Receipt (optional)</div>
                <div className="receipt-upload-row">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setNewReceiptFile(e.target.files?.[0] || null)}
                  />
                </div>
                {newReceiptMsg && (
                  <div className={`modal-message ${newReceiptMsg.includes('Failed') ? 'error' : 'success'}`}>
                    {newReceiptMsg}
                  </div>
                )}
              </div>

              <button type="submit" disabled={receiptUploading}>
                {receiptUploading ? 'Working…' : 'Create'}
              </button>
              <button type="button" className="modal-button close-button" onClick={() => setIsCreateModalOpen(false)}>
                Close
              </button>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="modal" onClick={cancelCopyToPayments}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-x"
              onClick={cancelCopyToPayments}
              aria-label="Close"
              title="Close"
            >
            </button>
            <h2>Confirm Copy</h2>
            <p>Are you sure you want to copy {selectedExpenses.length} selected expenses to payments?</p>
            <div className="modal-buttons">
              <button className="modal-button" onClick={copySelectedToPayments}>Yes</button>
              <button className="modal-button close-button" onClick={cancelCopyToPayments}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;